import { useQuery } from '@tanstack/react-query';
import { supabase as _supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from './useCurrentAppUser';
import type { DialerAttempt, DialerSession } from './usePowerDialer';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

export interface DialerSessionRow extends DialerSession {
  created_at: string;
  sdr?: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
}

export interface DialerSessionStats {
  dialed: number;
  human: number;
  machine: number;
  noAnswer: number;
  connected: number;
  humanRate: number;
  avgTimeToConnect: number | null;
  talkSeconds: number;
  durationSeconds: number | null;
}

export function computeSessionStats(
  session: DialerSessionRow | null,
  attempts: DialerAttempt[]
): DialerSessionStats {
  const dialed = attempts.length || session?.total_dialed || 0;
  const human = attempts.filter((a) => a.outcome === 'human').length;
  const machine = attempts.filter((a) => a.outcome === 'machine').length;
  const noAnswer = attempts.filter((a) => ['no_answer', 'busy', 'failed'].includes(a.outcome ?? '')).length;
  const connectedAttempts = attempts.filter((a) => !!a.connected_at);

  const connectDeltas = connectedAttempts
    .map((a) => (new Date(a.connected_at as string).getTime() - new Date(a.created_at).getTime()) / 1000)
    .filter((n) => Number.isFinite(n) && n >= 0);

  const talkSeconds = attempts.reduce((sum, a) => sum + (a.duration_seconds ?? 0), 0);

  const start = session?.started_at ? new Date(session.started_at).getTime() : null;
  const finish = session?.ended_at ? new Date(session.ended_at).getTime() : Date.now();

  return {
    dialed,
    human,
    machine,
    noAnswer,
    connected: connectedAttempts.length,
    humanRate: dialed > 0 ? (human / dialed) * 100 : 0,
    avgTimeToConnect: connectDeltas.length
      ? connectDeltas.reduce((a, b) => a + b, 0) / connectDeltas.length
      : null,
    talkSeconds,
    durationSeconds: start ? Math.max(0, Math.round((finish - start) / 1000)) : null,
  };
}

/** Detalhe de uma sessão (individual). */
export function useDialerSessionDetail(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['dialer-session-detail', sessionId],
    enabled: !!sessionId,
    queryFn: async (): Promise<{ session: DialerSessionRow | null; attempts: DialerAttempt[] }> => {
      const { data: session, error } = await supabase
        .from('dialer_sessions')
        .select('*, sdr:app_users!dialer_sessions_sdr_id_fkey(id, display_name, first_name, last_name, email)')
        .eq('id', sessionId)
        .maybeSingle();
      if (error) throw error;

      const { data: attempts, error: attemptsError } = await supabase
        .from('dialer_attempts')
        .select(
          `id, session_id, contact_id, to_e164, status, outcome, answered_by,
           connected_at, ended_at, duration_seconds, created_at,
           contact:contacts!dialer_attempts_contact_id_fkey(id, first_name, last_name)`
        )
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false });
      if (attemptsError) throw attemptsError;

      return { session: session ?? null, attempts: attempts ?? [] };
    },
  });
}

/** Histórico de sessões do SDR logado. */
export function useMyDialerSessions(days = 30) {
  const { data: currentUser } = useCurrentAppUser();
  const sdrId = currentUser?.id;

  return useQuery({
    queryKey: ['my-dialer-sessions', sdrId, days],
    enabled: !!sdrId,
    queryFn: async (): Promise<DialerSessionRow[]> => {
      const since = new Date();
      since.setDate(since.getDate() - days);
      const { data, error } = await supabase
        .from('dialer_sessions')
        .select('*')
        .eq('sdr_id', sdrId)
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Visão executiva: todas as sessões do período (requer admin via RLS). */
export function useAllDialerSessions(days = 7) {
  return useQuery({
    queryKey: ['all-dialer-sessions', days],
    queryFn: async (): Promise<DialerSessionRow[]> => {
      const since = new Date();
      since.setDate(since.getDate() - days);
      const { data, error } = await supabase
        .from('dialer_sessions')
        .select('*, sdr:app_users!dialer_sessions_sdr_id_fkey(id, display_name, first_name, last_name, email)')
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface DialerAttemptLite {
  id: string;
  sdr_id: string | null;
  outcome: string | null;
  connected_at: string | null;
  created_at: string;
  duration_seconds: number | null;
}

/** Tentativas do período para consolidar métricas executivas. */
export function useAllDialerAttempts(days = 7) {
  return useQuery({
    queryKey: ['all-dialer-attempts', days],
    queryFn: async (): Promise<DialerAttemptLite[]> => {
      const since = new Date();
      since.setDate(since.getDate() - days);
      const { data, error } = await supabase
        .from('dialer_attempts')
        .select('id, sdr_id, outcome, connected_at, created_at, duration_seconds')
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false })
        .limit(5000);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function formatDuration(seconds: number | null | undefined) {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}min`;
  }
  return m > 0 ? `${m}min ${s}s` : `${s}s`;
}
